
import Vue from "vue";
import Component from "vue-class-component";
import TableEmptySlot from "@/features/shared/components/tables/TableEmptySlot.vue";
import {
  OfficialQualificationLightViewModel,
  QualificationsClient,
  SortOrder,
  SpecialityType,
} from "@/api-client";
import ServersidePagingSelector from "@/components/ServersidePagingSelector.vue";
import { formatDate } from "@/formatting/dateFormatter";
import { BvTableCtxObject, BvTableFieldArray } from "bootstrap-vue";
import ClientsidePagingSelector from "@/components/ClientsidePagingSelector.vue";
import Loader from "@/features/shared/components/Loader";
import ServersidePaging from "@/components/ServersidePaging";
import { debounceFn } from "debounce-decorator-ts";
import PopoverInfo from "@/components/PopoverInfo.vue";
import FilterCheckbox from "@/components/FilterCheckbox.vue";
import ValidityStatusBadge from "@/features/shared/components/ValidityStatusBadge.vue";

class Filters {
  searchTerm: string;
  showInvalid!: boolean;
  showValid!: boolean;
  showUnchecked!: boolean;
  showTraining!: boolean;
  showEducational!: boolean;
  sort?: string;
  sortDir?: SortOrder;

  constructor(
    searchTerm?: string,
    showInvalid?: boolean,
    showValid?: boolean,
    showUnchecked?: boolean,
    showTraining?: boolean,
    showEducational?: boolean,
    sort?: string,
    sortDir?: SortOrder
  ) {
    this.searchTerm = searchTerm || "";
    this.showInvalid = showInvalid || false;
    this.showValid = showValid || true;
    this.showUnchecked = showUnchecked || false;
    this.showTraining = showTraining || false;
    this.showEducational = showEducational || false;
    this.sort = sort;
    this.sortDir = sortDir;
  }
}

@Component({
  components: {
    PopoverInfo,
    TableEmptySlot,
    ValidityStatusBadge,
    ClientsidePagingSelector,
    ServersidePagingSelector,
    FilterCheckbox,
  },
})
export default class QualificationTable extends Vue {
  qualifications: OfficialQualificationLightViewModel[] = [];
  SortOrder = SortOrder;
  dataLoaded = false;
  filters = new Filters();
  paging = new ServersidePaging();
  loader = new Loader(true);

  async created() {
    this.getParametersFromRouteOrDefault();

    this.$watch(
      () => this.filters,
      () => {
        this.paging.pageNumber = 1;
        this.reflectParametersToRoute();
      },
      { deep: true }
    );

    this.$watch(
      () => this.paging.pageNumber,
      () => {
        this.reflectParametersToRoute();
      }
    );

    this.$watch(
      () => this.paging.pageSize,
      () => {
        this.paging.pageNumber = 1;
        this.reflectParametersToRoute();
      }
    );

    this.$watch(
      () => this.$route.query,
      () => {
        this.getQualifications();
      },
      { deep: true, immediate: true }
    );
  }

  navigateToDetails(qualification: OfficialQualificationLightViewModel) {
    const routeMap: { [id: string]: string } = {};
    routeMap[SpecialityType.Educational] = "educational-details";
    routeMap[SpecialityType.Training] = "training-details";
    this.$router.push({
      name: routeMap[qualification.specialityType],
      params: {
        id: qualification.id,
        seafarerId: qualification.seafarerId,
      },
    });
  }

  async getQualifications() {
    this.qualifications = [];
    const result = await new QualificationsClient()
      .setLoader(this.loader)
      .qualifications(
        this.filters.searchTerm,
        this.paging.pageNumber,
        this.paging.pageSize,
        this.filters.showValid,
        this.filters.showInvalid,
        this.filters.showUnchecked,
        this.filters.showTraining,
        this.filters.showEducational,
        this.filters.sortDir ?? null,
        this.filters.sort ?? ""
      );
    this.paging.totalCount = result.totalCount;
    this.paging.pageSize = result.pageSize;
    this.qualifications = result.pageResult;
    this.dataLoaded = true;
  }

  @debounceFn(100) // Prevents possible double-update
  reflectParametersToRoute(): void {
    this.$router.replace({
      query: {
        // Setting to 'undefined' removes parameter from URL.
        searchTerm: this.filters.searchTerm || undefined,
        pageNumber: this.paging.pageNumber
          ? this.paging.pageNumber.toString()
          : undefined,
        pageSize: this.paging.pageSize
          ? this.paging.pageSize.toString()
          : undefined,
        showInvalid: this.filters.showInvalid.toString(),
        showValid: this.filters.showValid.toString(),
        showUnchecked: this.filters.showUnchecked.toString(),
        showTraining: this.filters.showTraining.toString(),
        showEducational: this.filters.showEducational.toString(),
        sort: this.filters?.sort ?? "",
        sortDir: this.filters?.sortDir ?? SortOrder.DESC,
      },
    });
  }

  updateSort(ctx: BvTableCtxObject) {
    // this.filters.sort = ctx.sortDesc ? ApplicationSortField.DateSubmittedDesc : ApplicationSortField.DateSubmittedAsc;
    this.filters.sort = ctx.sortBy ?? undefined;
    this.filters.sortDir = ctx.sortDesc ? SortOrder.DESC : SortOrder.ASC;
  }

  getParametersFromRouteOrDefault() {
    const route = this.$route as any;
    const searchTerm: string | undefined = route.query.searchTerm;
    const showInvalid: boolean | undefined = route.query.showInvalid;
    const showValid: boolean | undefined = route.query.showValid;
    const showUnchecked: boolean | undefined = route.query.showUnchecked;
    const showTraining: boolean | undefined = route.query.showTraining;
    const showEducational: boolean | undefined = route.query.showEducational;
    const sort: string | undefined = route.query.sort;
    const sortDir: SortOrder.DESC | SortOrder.ASC | undefined =
      route.query.sortDir;

    this.filters = new Filters(
      searchTerm,
      showInvalid,
      showValid,
      showUnchecked,
      showTraining,
      showEducational,
      sort,
      sortDir
    );

    this.paging.pageNumber =
      parseInt(route.query.pageNumber) || this.paging.pageNumber;
    this.paging.pageSize =
      parseInt(route.query.pageSize) || this.paging.pageSize;
  }

  fields: BvTableFieldArray = [
    {
      key: "documentNumber",
      label: this.$ls.Global_Label_DocumentNumber,
      sortable: true,
    },
    {
      key: "specialityType",
      label: this.$ls.Documents_Qualifications_Table_Label_SpecialityType,
      sortable: true,
      sortByFormatted: true,
      formatter: (
        value: any,
        key: any,
        item: OfficialQualificationLightViewModel
      ) => {
        return item.specialityType == SpecialityType.Training
          ? this.$ls.Documents_Qualifications_SpecialityType_TrainingCourses
          : this.$ls.Documents_Qualifications_SpecialityType_FormalEducation;
      },
    },
    {
      key: "seafarerName",
      label: this.$ls.Global_Label_Seafarer,
    },
    {
      key: "birthDate",
      label: this.$ls.Global_Label_DateOfBirth,
      formatter: (
        value: any,
        key: string,
        item: OfficialQualificationLightViewModel
      ): string => formatDate(item.birthDate),
    },
    {
      key: "name",
      label: this.$ls.Global_Label_Name,
      sortable: true,
      thClass: "w-20",
    },
    {
      key: "issuedAt",
      label: this.$ls.Global_Label_IssueDateShort,
      sortable:true,
      formatter: (
        value: any,
        key: string,
        item: OfficialQualificationLightViewModel
      ): string => formatDate(item.issuedAt),
    },
    {
      key: "validUntil",
      label: this.$ls.Global_Label_ValidUntil,
      sortable: true,
      formatter: (
        value: any,
        key: string,
        item: OfficialQualificationLightViewModel
      ): string => formatDate(item.validUntil),
    },

    {
      key: "additionalInformation",
      thClass: "w-10",
      label: this.$ls.Global_Label_AdditionalInfo,
      sortable: true,
    },
    {
      key: "statusString",
      sortable:true,
      label: this.$ls.Global_Label_DocumentState,
    },
    {
      key: "linkIcon",
      label: "",
      thClass: "w-5",
      tdClass: "table-actions",
    },
  ];
}
